import { useState, useMemo, useCallback } from 'react'
import { onlyUnique } from '../../../util'

const toSafeValue = value => (Array.isArray(value) ? value.map(value => value) : [])

const toSafeOptions = options =>
  Array.isArray(options) ? options.map(option => ({ label: option?.label, value: option?.value, parents: option?.parents })) : []

const getExcludeFilterByValues = values => option => values.indexOf(option.value) === -1
const getIncludeFilterByValues = values => option => values.indexOf(option.value) > -1

const useDualList = props => {
  const { value, onChange, options, disabled, unselectedFilter, selectedFilter } = props

  const safeValue = useMemo(() => toSafeValue(value), [value])
  const safeOptions = useMemo(() => toSafeOptions(options), [options])

  const unselectedOptions = useMemo(
    () => safeOptions.filter(getExcludeFilterByValues(safeValue)),
    [safeOptions, safeValue],
  )

  const selectedOptions = useMemo(
    () => safeOptions.filter(getIncludeFilterByValues(safeValue)),
    [safeOptions, safeValue],
  )

  const [itemsToAdd, setItemsToAdd] = useState([])
  const [itemsToRemove, setItemsToRemove] = useState([])

  const handleSelectedItemsToAddChange = useCallback(values => {
    setItemsToAdd(values)
    setItemsToRemove([])
  }, [])

  const handleAddAllClick = useCallback(() => {
    let optionsToAdd = unselectedOptions

    if (unselectedFilter && typeof unselectedFilter === 'string' && unselectedFilter.trim() !== '') {
      const filterLower = unselectedFilter.toLowerCase()
      optionsToAdd = optionsToAdd.filter(option =>
        option.label.toLowerCase().includes(filterLower) ||
        option.parents?.some(parent => parent.label.toLowerCase().includes(filterLower))
      )
    }

    if (optionsToAdd.length > 0) {
      const combinedValues = [...selectedOptions.map(option => option.value), ...optionsToAdd.map(option => option.value)].filter(onlyUnique)
      onChange(combinedValues)
      setItemsToAdd([])
    }
  }, [unselectedOptions, selectedOptions, onChange, unselectedFilter])

  const handleAddAllParentsClick = useCallback(() => {
    const valuesWithParents = unselectedOptions
      .filter(option => option.parents && option.parents.length > 0)
      .map(option => option.value)

    // Combine existing selected values with new values, ensuring uniqueness
    const combinedValues = [...selectedOptions.map(option => option.value), ...valuesWithParents].filter(onlyUnique)

    onChange(combinedValues)
    setItemsToAdd([])
  }, [unselectedOptions, selectedOptions, onChange])

  const handleAddClick = useCallback(() => {
    onChange([...itemsToAdd, ...safeValue])
    setItemsToAdd([])
  }, [safeValue, itemsToAdd, onChange])

  const handleSelectedItemsToRemoveChange = useCallback(values => {
    setItemsToRemove(values)
    setItemsToAdd([])
  }, [])

  const handleRemoveClick = useCallback(() => {
    const filteredValues = safeValue.filter(value => itemsToRemove.indexOf(value) === -1)

    onChange(filteredValues)
    setItemsToRemove([])
  }, [safeValue, itemsToRemove, onChange])

  const handleRemoveAllClick = useCallback(() => {
    let optionsToRemove = selectedOptions

    if (selectedFilter && typeof selectedFilter === 'string' && selectedFilter.trim() !== '') {
      const filterLower = selectedFilter.toLowerCase()
      optionsToRemove = optionsToRemove.filter(option =>
        option.label.toLowerCase().includes(filterLower) ||
        option.parents?.some(parent => parent.label.toLowerCase().includes(filterLower))
      )
    }

    const valuesToRemove = optionsToRemove.map(option => option.value)
    const newSelection = safeValue.filter(val => !valuesToRemove.includes(val))
    onChange(newSelection)
    setItemsToRemove([])
  }, [onChange, selectedFilter, selectedOptions, safeValue])

  const isAddAllBtnDisabled = unselectedOptions.length === 0 || disabled
  const isAddAllParentsBtnDisabled = unselectedOptions.filter(option => option.parents && option.parents.length > 0).length === 0 || disabled
  const isAddBtnDisabled = isAddAllBtnDisabled || itemsToAdd.length === 0
  const isRemoveAllBtnDisabled = selectedOptions.length === 0 || disabled
  const isRemoveBtnDisabled = isRemoveAllBtnDisabled || itemsToRemove.length === 0

  const handleDropUnselectedItems = useCallback(
    items => {
      const values = items.map(item => item.value)
      onChange([...values, ...safeValue].filter(onlyUnique))
      setItemsToAdd([])
    },
    [safeValue, onChange],
  )

  const handleDropSelectedItems = useCallback(
    items => {
      const values = items.map(item => item.value)

      onChange(safeValue.filter(safeValue => values.indexOf(safeValue) === -1).filter(onlyUnique))
      setItemsToRemove([])
    },
    [safeValue, onChange],
  )

  const handleAddMultipleItems = useCallback(values => {
    if (!Array.isArray(values) || values.length === 0) return;
    const combinedValues = [...safeValue, ...values].filter(onlyUnique);
    onChange(combinedValues);
    setItemsToAdd([]);
  }, [safeValue, onChange]);

  const handleRemoveMultipleItems = useCallback(values => {
    if (!Array.isArray(values) || values.length === 0) return;
    const newSelection = safeValue.filter(val => !values.includes(val));
    onChange(newSelection);
    setItemsToRemove([]);
  }, [safeValue, onChange]);

  return {
    unselectedOptions,
    selectedOptions,
    itemsToAdd,
    itemsToRemove,
    handleSelectedItemsToAddChange,
    handleAddAllClick,
    handleAddAllParentsClick,
    isAddAllBtnDisabled,
    isAddAllParentsBtnDisabled,
    handleAddClick,
    isAddBtnDisabled,
    handleSelectedItemsToRemoveChange,
    handleRemoveClick,
    isRemoveAllBtnDisabled,
    handleRemoveAllClick,
    isRemoveBtnDisabled,
    handleDropUnselectedItems,
    handleDropSelectedItems,
    handleAddMultipleItems,
    handleRemoveMultipleItems,
  }
}

export default useDualList
